import { loadSewMimicModule, getSewMimicModule, type SewMimicInitOptions, type SewMimicModule } from './runtime';

type Vec3Input = ReadonlyArray<number> | Float32Array | Float64Array;

/**
 * Finger landmark positions for a single finger.
 * All positions are expressed in wrist frame coordinates.
 */
export interface FingerLandmarks {
	mcp: Vec3Input;
	pip: Vec3Input;
	tip: Vec3Input;
}

/**
 * Finger tracking positions for the Ability Hand.
 * At minimum, `index.mcp` and `pinky.mcp` must be present to compute the palm frame.
 */
export interface FingerPositions {
	thumb?: FingerLandmarks;
	index?: FingerLandmarks;
	middle?: FingerLandmarks;
	ring?: FingerLandmarks;
	pinky?: FingerLandmarks;
}

export interface HandIKRequest {
	fingers: FingerPositions;
	/**
	 * Filter alpha for temporal smoothing.
	 * 1.0 = no filtering (raw IK output), lower values apply smoothing.
	 * @default 1.0
	 */
	filterAlpha?: number;
}

/**
 * Joint angles for a single finger: [q1, q2]
 */
export type FingerJointAngles = [number, number];

/**
 * IK solution containing joint angles for each finger.
 */
export interface HandIKSolution {
	thumb?: FingerJointAngles;
	index?: FingerJointAngles;
	middle?: FingerJointAngles;
	ring?: FingerJointAngles;
	pinky?: FingerJointAngles;
}

export interface AHJointSolution {
	fingers: HandIKSolution;
	/** Dense actuator-indexed qpos array (10 elements: 2 joints × 5 fingers) */
	control_commands?: number[];
}

export interface AHJointGroupMetadata {
	thumb?: { qpos: number[]; qvel: number[]; joint_ids: number[]; actuator_ids: number[] };
	index?: { qpos: number[]; qvel: number[]; joint_ids: number[]; actuator_ids: number[] };
	middle?: { qpos: number[]; qvel: number[]; joint_ids: number[]; actuator_ids: number[] };
	ring?: { qpos: number[]; qvel: number[]; joint_ids: number[]; actuator_ids: number[] };
	pinky?: { qpos: number[]; qvel: number[]; joint_ids: number[]; actuator_ids: number[] };
}

export class SewMimicAHController {
	private module: SewMimicModule | null = null;

	async initialize(options: SewMimicInitOptions = {}): Promise<void> {
		this.module = await loadSewMimicModule(options);
	}

	isReady(): boolean {
		return this.module !== null || getSewMimicModule() !== null;
	}

	/**
	 * Solves inverse kinematics for the Ability Hand from finger positions.
	 *
	 * @param request - Finger positions in wrist frame and optional filter alpha
	 * @returns Joint solution with per-finger angles and actuator commands
	 */
	async solveIK(request: HandIKRequest): Promise<AHJointSolution> {
		const module = this.ensureModule();

		// Convert finger positions to the format expected by the WASM module
		const fingersInput = this.convertFingerPositions(request.fingers);

		// Call the WASM IK solver
		const ikResult = (module as unknown as {
			ah_ik_from_positions: (fingers: unknown, filterAlpha?: number | null) => unknown;
		}).ah_ik_from_positions(fingersInput, request.filterAlpha ?? 1.0);

		// Parse the IK result - returns { thumb: [q1, q2], index: [q1, q2], ... }
		const solution = this.parseIKResult(ikResult);

		const result: AHJointSolution = {
			fingers: solution
		};

		// Build the spec for actuator qpos mapping
		const spec = this.buildActuatorSpec(solution);

		// Get the actuator control commands (10-element array)
		const qpos = (module as unknown as {
			ah_to_actuator_qpos: (spec: unknown) => Float64Array;
		}).ah_to_actuator_qpos(spec);

		result.control_commands = Array.from(qpos);

		return result;
	}

	/**
	 * Gets the current joint group metadata for the Ability Hand.
	 * Keys: thumb, index, middle, ring, pinky
	 * Each contains: qpos, qvel, joint_ids, actuator_ids
	 */
	getJointGroupMetadata(): AHJointGroupMetadata {
		const module = this.ensureModule();
		return (module as unknown as {
			ah_get_joint_group_metadata: () => AHJointGroupMetadata;
		}).ah_get_joint_group_metadata();
	}

	/**
	 * Overrides the joint group metadata from a JSON string.
	 */
	setJointGroupMetadata(json: string): void {
		const module = this.ensureModule();
		(module as unknown as {
			ah_set_joint_group_metadata: (json: string) => void;
		}).ah_set_joint_group_metadata(json);
	}

	/**
	 * Resets the joint group metadata to the embedded default.
	 */
	resetJointGroupMetadata(): void {
		const module = this.ensureModule();
		(module as unknown as {
			ah_reset_joint_group_metadata: () => void;
		}).ah_reset_joint_group_metadata();
	}

	destroy(): void {
		this.module = null;
	}

	private ensureModule(): SewMimicModule {
		if (!this.module) {
			const cached = getSewMimicModule();
			if (!cached) {
				throw new Error('SEW-Mimic runtime not initialized. Call initialize() first.');
			}
			this.module = cached;
		}
		return this.module;
	}

	private convertFingerPositions(fingers: FingerPositions): Record<string, unknown> {
		const result: Record<string, unknown> = {};

		const fingerNames = ['thumb', 'index', 'middle', 'ring', 'pinky'] as const;

		for (const name of fingerNames) {
			const finger = fingers[name];
			if (finger) {
				result[name] = {
					mcp: toNumberArray(finger.mcp),
					pip: toNumberArray(finger.pip),
					tip: toNumberArray(finger.tip)
				};
			}
		}

		return result;
	}

	private parseIKResult(ikResult: unknown): HandIKSolution {
		const result: HandIKSolution = {};

		if (typeof ikResult !== 'object' || ikResult === null) {
			return result;
		}

		const raw = ikResult as Record<string, unknown>;
		const fingerNames = ['thumb', 'index', 'middle', 'ring', 'pinky'] as const;

		for (const finger of fingerNames) {
			const fingerData = raw[finger];
			// Result format is [q1, q2] per finger
			if (Array.isArray(fingerData) && fingerData.length >= 2) {
				result[finger] = [Number(fingerData[0]), Number(fingerData[1])];
			}
		}

		return result;
	}

	private buildActuatorSpec(solution: HandIKSolution): Record<string, number[]> {
		const spec: Record<string, number[]> = {};
		const fingerNames = ['thumb', 'index', 'middle', 'ring', 'pinky'] as const;

		for (const finger of fingerNames) {
			const fingerSolution = solution[finger];
			if (fingerSolution) {
				spec[finger] = [fingerSolution[0], fingerSolution[1]];
			}
		}

		return spec;
	}
}

function toNumberArray(value: Vec3Input): number[] {
	if (value instanceof Float32Array || value instanceof Float64Array) {
		return Array.from(value);
	}
	return [...value];
}
